import React, { useState } from 'react';
import { X, ChevronRight, MapPin, Heart, DollarSign, Calendar, Users } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

const TravelQuizModal = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);

  const questions = [
    {
      id: 'travelStyle',
      question: 'What kind of travel experience excites you most?',
      options: [
        { value: 'luxury', label: 'Luxury & Comfort', icon: Heart },
        { value: 'adventure', label: 'Adventure & Exploration', icon: MapPin },
        { value: 'cultural', label: 'Cultural Immersion', icon: Users },
        { value: 'relaxation', label: 'Relaxation & Wellness', icon: Calendar }
      ]
    },
    {
      id: 'budget',
      question: 'What\'s your preferred budget range per person?',
      options: [
        { value: 'budget', label: 'Under ₹1,50,000', icon: DollarSign },
        { value: 'mid', label: '₹1,50,000 - ₹4,00,000', icon: DollarSign },
        { value: 'premium', label: '₹4,00,000 - ₹8,00,000', icon: DollarSign },
        { value: 'luxury', label: '₹8,00,000+', icon: DollarSign }
      ]
    },
    {
      id: 'duration',
      question: 'How long would you like to travel?',
      options: [
        { value: 'short', label: '3-5 days', icon: Calendar },
        { value: 'week', label: '1 week', icon: Calendar },
        { value: 'extended', label: '2-3 weeks', icon: Calendar },
        { value: 'long', label: 'More than 3 weeks', icon: Calendar }
      ]
    }
  ];

  const handleAnswer = (questionId, answer) => {
    setAnswers(prev => ({ ...prev, [questionId]: answer }));
    
    if (currentStep < questions.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      // Calculate result
      calculateResult();
    }
  };

  const calculateResult = () => {
    const { travelStyle, budget, duration } = answers;
    
    let recommendedDestination = 'Dubai';
    let recommendedType = 'Luxury';
    
    // Simple recommendation logic
    if (travelStyle === 'luxury' || budget === 'luxury') {
      recommendedDestination = 'Maldives';
      recommendedType = 'Luxury';
    } else if (travelStyle === 'adventure') {
      recommendedDestination = 'Nepal';
      recommendedType = 'Adventure';
    } else if (travelStyle === 'cultural') {
      recommendedDestination = 'Vietnam';
      recommendedType = 'Cultural';
    } else if (travelStyle === 'relaxation') {
      recommendedDestination = 'Bali';
      recommendedType = 'Medical Tourism';
    }
    
    setResult({
      destination: recommendedDestination,
      type: recommendedType,
      description: getDestinationDescription(recommendedDestination)
    });
  };

  const getDestinationDescription = (destination) => {
    const descriptions = {
      'Maldives': 'Perfect for luxury seekers with overwater villas and pristine beaches.',
      'Nepal': 'Ideal for adventure enthusiasts with Himalayan treks and cultural experiences.',
      'Vietnam': 'Great for cultural immersion with rich history and authentic experiences.',
      'Bali': 'Perfect for wellness and relaxation with spiritual retreats and beautiful temples.',
      'Dubai': 'Excellent for luxury experiences with world-class amenities and innovation.'
    };
    return descriptions[destination] || 'A perfect destination for your travel style!';
  };

  const resetQuiz = () => {
    setCurrentStep(0);
    setAnswers({});
    setResult(null);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="text-2xl font-bold text-gray-900">
            {result ? 'Your Perfect Trip Awaits!' : 'Travel Personality Quiz'}
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={20} />
          </Button>
        </CardHeader>

        <CardContent className="pb-6">
          {!result ? (
            <>
              {/* Progress bar */}
              <div className="mb-8">
                <div className="flex justify-between text-sm text-gray-600 mb-2">
                  <span>Question {currentStep + 1} of {questions.length}</span>
                  <span>{Math.round(((currentStep + 1) / questions.length) * 100)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-gradient-to-r from-[#6C30ED] to-[#4E8EFF] h-2 rounded-full transition-all duration-300"
                    style={{ width: `${((currentStep + 1) / questions.length) * 100}%` }}
                  />
                </div>
              </div>

              {/* Current question */}
              <div className="mb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-6">
                  {questions[currentStep].question}
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {questions[currentStep].options.map((option) => {
                    const IconComponent = option.icon;
                    return (
                      <button
                        key={option.value}
                        onClick={() => handleAnswer(questions[currentStep].id, option.value)}
                        className="p-4 border-2 border-gray-200 rounded-lg hover:border-[#6C30ED] hover:bg-[#6C30ED]/5 transition-all duration-200 text-left group"
                      >
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-[#6C30ED]/10 rounded-full flex items-center justify-center mr-4 group-hover:bg-[#6C30ED]/20">
                            <IconComponent size={20} className="text-[#6C30ED]" />
                          </div>
                          <span className="font-medium text-gray-900">{option.label}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          ) : (
            /* Results */
            <div className="text-center">
              <div className="mb-8">
                <div className="w-20 h-20 bg-gradient-to-r from-[#6C30ED] to-[#4E8EFF] rounded-full flex items-center justify-center mx-auto mb-4">
                  <MapPin size={32} className="text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  We Recommend: {result.destination}
                </h3>
                <p className="text-lg text-gray-600 mb-4">
                  Perfect for {result.type} Travel
                </p>
                <p className="text-gray-700 leading-relaxed mb-8">
                  {result.description}
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  size="lg"
                  className="bg-gradient-to-r from-[#FE4B79] to-[#6C30ED] hover:from-[#fe3667] hover:to-[#5c28d4] text-white font-semibold px-8 py-3"
                  onClick={() => {
                    onClose();
                    window.location.href = `/destinations/${result.destination.toLowerCase()}`;
                  }}
                >
                  Explore {result.destination}
                  <ChevronRight size={18} className="ml-2" />
                </Button>
                <Button 
                  variant="outline"
                  size="lg"
                  onClick={resetQuiz}
                  className="border-[#6C30ED] text-[#6C30ED] hover:bg-[#6C30ED] hover:text-white px-8 py-3"
                >
                  Retake Quiz
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TravelQuizModal;